import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { categoryImage } from "@/lib/assets";
import { ProductCard } from "@/components/ProductCard";

const PAGE_SIZE = 12;

const genericIntro = (name) =>
  `Discover our handpicked selection of ${name.toLowerCase()} — authentic African groceries and essentials, kept in sync with our store shelves and delivered fresh across the city.`;

export default function CategoryPage() {
  const { slug } = useParams();
  const [category, setCategory] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const skipRef = useRef(0);
  const fetchingRef = useRef(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setCategory(null);
    setNotFound(false);
    setProducts([]);
    setLoading(true);
    api.get(`/categories/${slug}`).then((r) => setCategory(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const fetchProducts = useCallback(
    async (reset) => {
      if (!category || fetchingRef.current) return;
      fetchingRef.current = true;
      if (reset) { skipRef.current = 0; setLoading(true); } else { setLoadingMore(true); }
      try {
        const r = await api.get("/products", {
          params: { category: category.name, skip: reset ? 0 : skipRef.current, limit: PAGE_SIZE, sort: "featured" },
        });
        const batch = r.data;
        setProducts((prev) => (reset ? batch : [...prev, ...batch]));
        skipRef.current = (reset ? 0 : skipRef.current) + batch.length;
        setHasMore(batch.length === PAGE_SIZE);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        fetchingRef.current = false;
      }
    },
    [category]
  );

  useEffect(() => { if (category) fetchProducts(true); }, [category, fetchProducts]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !fetchingRef.current) fetchProducts(false);
      },
      { rootMargin: "400px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, fetchProducts, products.length]);

  if (notFound)
    return (
      <div className="mx-auto max-w-3xl px-5 py-32 text-center">
        <p className="font-display text-4xl text-forest">Category not found</p>
        <Link to="/shop" className="mt-6 inline-flex items-center gap-2 text-clay hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </Link>
      </div>
    );

  return (
    <div data-testid="category-page">
      {/* Banner */}
      <section className="px-3 pt-3 sm:px-5 lg:px-8">
        <div className="relative mx-auto h-72 max-w-[1440px] overflow-hidden rounded-[2rem] bg-forest lg:h-80 lg:rounded-[2.5rem]">
          {category && (
            <img src={categoryImage(category)} alt={category.name} className="absolute inset-0 h-full w-full object-cover opacity-50" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/70 to-forest/10" />
          <div className="relative flex h-full flex-col justify-end px-6 pb-10 sm:px-10 lg:px-14">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Link to="/shop" className="mb-4 flex w-fit items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm font-semibold text-sand backdrop-blur transition-colors hover:bg-white/20">
                <ArrowLeft className="h-4 w-4" /> All categories
              </Link>
              <span className="label-eyebrow text-ochre">Category</span>
              <h1 data-testid="category-title" className="mt-1 font-display text-5xl font-semibold tracking-tight text-white lg:text-6xl">
                {category?.name || "…"}
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1440px] px-5 py-10 lg:px-10">
        {category && (
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            data-testid="category-intro"
            className="max-w-2xl text-base leading-relaxed text-muted-foreground"
          >
            {category.description?.trim() || genericIntro(category.name)}
          </motion.p>
        )}

        {loading ? (
          <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] animate-pulse rounded-[1.75rem] bg-muted" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="mt-16 text-center" data-testid="category-empty">
            <p className="font-display text-3xl text-forest">Nothing here yet</p>
            <p className="mt-2 text-muted-foreground">Check back soon — our shelves restock often.</p>
          </div>
        ) : (
          <>
            <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i % PAGE_SIZE} />
              ))}
            </div>
            <div ref={sentinelRef} className="h-12" />
            {loadingMore && (
              <div className="mt-4 flex justify-center" data-testid="category-loading-more">
                <Loader2 className="h-6 w-6 animate-spin text-clay" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
